import { db } from "./db";
import { freshProgress, getVocab } from "./seed";
import { me, type MeProgressResponse } from "./api";
import { DIRECTIONS, type ProgressRecord, type SettingsMeta, type ThemePref } from "./types";

const WIPE_FLAG = "ppl-v6-local-wiped";

function alreadyWiped(): boolean {
  try {
    return localStorage.getItem(WIPE_FLAG) === "1";
  } catch {
    return false;
  }
}

function markWiped(): void {
  try {
    localStorage.setItem(WIPE_FLAG, "1");
  } catch {
    /* private browsing — accept the cost: we will wipe again on next load */
  }
}

/**
 * One-time clean cut: when a user first signs in after the Phase-4 deploy, any
 * pre-existing local IDB state is dropped. Spec §2 ("alle starten bei null").
 * Subsequent logins keep IDB as the working cache and only the outbox is wiped
 * by logout.
 */
export async function wipeLocalIfNeeded(): Promise<boolean> {
  if (alreadyWiped()) return false;
  await db.transaction("rw", db.progress, db.meta, db.outbox, db.markers, async () => {
    await db.progress.clear();
    await db.meta.clear();
    await db.outbox.clear();
    await db.markers.clear();
  });
  markWiped();
  return true;
}

/**
 * Wipes IDB unconditionally — called on logout.
 */
export async function wipeLocalAll(): Promise<void> {
  await db.transaction("rw", db.progress, db.meta, db.outbox, db.markers, async () => {
    await db.progress.clear();
    await db.meta.clear();
    await db.outbox.clear();
    await db.markers.clear();
  });
}

function applyServerSettings(
  current: SettingsMeta,
  server: MeProgressResponse["settings"],
): SettingsMeta {
  return {
    ...current,
    theme: server.theme as ThemePref,
    sound: server.sound,
    learningDirection: server.learningDirection,
    learningMode: server.learningMode,
  };
}

async function readOrCreateSettings(): Promise<SettingsMeta> {
  const existing = await db.meta.get("settings");
  if (existing && existing.key === "settings") return existing;
  return {
    key: "settings",
    rngSeed: Math.floor(Math.random() * 2 ** 31) || 0x9e3779b9,
    reduceMotion: false,
    theme: "system",
    sound: false,
    learningDirection: "en_de",
    learningMode: "all",
  };
}

/**
 * Replace local IDB progress with the server's snapshot. Vocab entries that
 * don't appear in the server response get fresh-progress rows (a new device
 * or a vocab.json change since last sync).
 */
export async function hydrateFromServer(): Promise<MeProgressResponse> {
  const data = await me.getProgress();

  const vocab = getVocab();
  // Phase 2 (#95): Server-Rows kommen pro (english, direction). Indexieren
  // entsprechend, dann pro Vokabel pro Direction die Server-Row übernehmen
  // oder freshProgress generieren.
  const serverByKey = new Map<string, ProgressRecord>();
  for (const p of data.progress as ProgressRecord[]) {
    serverByKey.set(`${p.english}|${p.direction}`, p);
  }

  const settings = applyServerSettings(await readOrCreateSettings(), data.settings);

  await db.transaction("rw", db.progress, db.meta, db.markers, async () => {
    await db.progress.clear();
    const rows: ProgressRecord[] = [];
    for (const entry of vocab) {
      for (const direction of DIRECTIONS) {
        const key = `${entry.english}|${direction}`;
        const serverRow = serverByKey.get(key);
        rows.push(serverRow ?? freshProgress(entry, direction));
      }
    }
    if (rows.length > 0) await db.progress.bulkAdd(rows);

    await db.meta.put(settings);

    // Issue #123: Marker-Liste vom Server als canonical-state übernehmen.
    // Server-Stand kommt als string-Array; lokales Datum nicht persistiert
    // (Server liefert nur die englischen Wörter), wir setzen createdAt
    // auf Hydrate-Zeit als best-effort.
    await db.markers.clear();
    const now = Date.now();
    const markerRows = data.markers.map((english) => ({ english, createdAt: now }));
    if (markerRows.length > 0) await db.markers.bulkAdd(markerRows);

    const maxIndex = rows.reduce((m, r) => Math.max(m, r.earliestQuestionIndex ?? 0), 0);
    await db.meta.put({
      key: "session",
      questionsAnswered: maxIndex,
      startedAt: Date.now(),
      totalElapsedMs: 0,
    });
  });

  return data;
}
